/**
 * SessionEndedOverlay Component
 * Shown after a live avatar session ends, with feedback and restart options
 */

import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitConversationFeedback } from '../../services/chatService';
import { sendContactMessage } from '../../services/contactService';
import logger from '../../utils/logger';
import './SessionEndedOverlay.css';

interface SessionEndedOverlayProps {
  companionImage?: string;
  companionName?: string;
  avatarId: string;
  conversationId: string | null;
  onStartNewSession: () => void;
  onClose: () => void;
}

/** Overlay displayed after a live session ends with star rating + review form */
export const SessionEndedOverlay: React.FC<SessionEndedOverlayProps> = ({
  companionImage,
  companionName,
  avatarId,
  conversationId,
  onStartNewSession,
  onClose,
}) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return;

    setIsSubmitting(true);
    setSubmitError(false);

    try {
      if (conversationId) {
        // Submit specific conversation feedback
        await submitConversationFeedback(avatarId, conversationId, rating, review || undefined);
      } else {
        // Fallback: Submit general feedback about the avatar/session if no conversationId exists
        const message = `[Session Feedback for ${companionName || avatarId}]\nRating: ${rating} stars\n\nReview: ${review || 'No review provided.'}`;
        await sendContactMessage('Session User', 'anonymous-session-feedback@theychat.ai', message);
      }
      setSubmitted(true);
    } catch (error) {
      logger.error('SessionEndedOverlay', 'Failed to submit feedback', error as Error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="session-ended-overlay">
      <div className="session-ended-content">
        {/* Avatar */}
        {companionImage && (
          <img
            src={companionImage}
            alt={companionName ?? 'Avatar'}
            className="session-ended-avatar"
          />
        )}

        {/* Title */}
        <h2 className="session-ended-title">{t('session.ended', 'Session Ended')}</h2>

        {/* Description */}
        <p className="session-ended-desc">
          {t(
            'session.endedDesc',
            'Your conversation with {{name}} has ended. Would you like to chat again?',
            { name: companionName ?? 'the companion' },
          )}
        </p>

        {/* Action Buttons */}
        <div className="session-ended-actions">
          <button type="button" className="session-ended-btn-primary" onClick={onStartNewSession}>
            {t('session.startNew', 'Start a New Session')}
          </button>
          <button type="button" className="session-ended-btn-secondary" onClick={onClose}>
            {t('session.closeSession', 'Close')}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="session-ended-divider">✦</div>

        {/* Feedback Section */}
        <div className="session-ended-feedback">
          <p className="session-ended-feedback-label">
            {t('session.feedbackPrompt', 'Enjoyed our chat? Share your thoughts!')}
          </p>

          {/* Star Rating */}
          <div className="session-ended-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`session-ended-star ${star <= rating ? 'active' : ''}`}
                onClick={() => setRating(star)}
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
                disabled={submitted}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    fill={star <= rating ? '#c49a6c' : 'none'}
                    stroke={star <= rating ? '#c49a6c' : '#d4d4d4'}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ))}
          </div>

          {/* Review Textarea */}
          {!submitted ? (
            <>
              <textarea
                className="session-ended-review"
                placeholder={t('session.writePlaceholder', 'Write a review...')}
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={2}
                disabled={isSubmitting}
              />
              {submitError && (
                <span className="session-ended-error">
                  {t('session.submitError', 'Failed to submit feedback. Please try again.')}
                </span>
              )}
              <button
                type="button"
                className="session-ended-submit"
                onClick={handleSubmit}
                disabled={rating === 0 || isSubmitting}
              >
                {isSubmitting
                  ? t('session.submitting', 'Submitting...')
                  : t('session.submit', 'Submit')}
              </button>
            </>
          ) : (
            <span className="session-ended-thanks">
              {t('session.thankYou', 'Thank you for your feedback! ✨')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionEndedOverlay;
